// src/store/materialScoreStore.js
import { defineStore } from 'pinia'
import { shallowRef, computed, readonly } from 'vue'
import { logInfo, logError, logWarn, logDebug } from '@/utils/logHelper'
import materialScoreApi from '@/common/api/cache/materialScoreApi'

/* =========================================================
 * 상수 정의
 * ======================================================= */
const STORE_NAME = 'materialScore'
const LOG_TAG = `[${STORE_NAME}Store]`

const ERRORS = Object.freeze({
  INVALID_RESPONSE: 'API 응답 형식이 올바르지 않습니다',
  INVALID_STRUCTURE: '데이터 구조가 올바르지 않습니다',
  FETCH_FAILED: '점수 데이터 조회 실패',
  RELOAD_FAILED: '점수 데이터 리로드 실패',
})

// ✅ 필수 필드 정의 (API 스펙에 맞게 수정)
const REQUIRED_FIELDS = ['materialId', 'score']

/* =========================================================
 * Pinia Store 정의
 * ======================================================= */
export const useMaterialScoreStore = defineStore(STORE_NAME, () => {
  /* =========================================================
   * 1) State
   * ======================================================= */
  const materialScoreData = shallowRef([])
  const loaded = shallowRef(false)
  const loading = shallowRef(false)
  const error = shallowRef(null)           // ✅ 추가: 에러 상태
  const lastUpdated = shallowRef(null)     // ✅ 추가: 마지막 갱신 시간
  
  let fetchPromise = null

  /* =========================================================
   * 2) Computed (Getters)
   * ======================================================= */
  /**
   * 점수 데이터 리스트 (읽기 전용)
   */
  const materialScoreList = computed(() => materialScoreData.value)

  /**
   * 로딩 중 여부
   */
  const isLoading = computed(() => loading.value)

  /**
   * 데이터 로드 완료 여부
   */
  const isLoaded = computed(() => loaded.value)

  /**
   * 데이터 개수
   */
  const dataCount = computed(() => materialScoreData.value.length)

  /**
   * ✅ 추가: 데이터 비어있음 여부
   */
  const isEmpty = computed(() => materialScoreData.value.length === 0)

  /**
   * ✅ 추가: 데이터 존재 여부
   */
  const hasData = computed(() => materialScoreData.value.length > 0)

  /**
   * ✅ 추가: 에러 발생 여부
   */
  const hasError = computed(() => error.value !== null)

  /**
   * ✅ 추가: 에러 메시지
   */
  const errorMessage = computed(() => error.value?.message || null)

  /* =========================================================
   * 3) Internal Helpers
   * ======================================================= */
  /**
   * ✅ 개선: 샘플 아이템 구조 검증
   * @param {object} sample - 첫 번째 아이템
   */
  function validateSampleItem(sample) {
    if (!sample || typeof sample !== 'object') {
      logWarn(`${LOG_TAG} 샘플 아이템이 객체가 아님:`, sample)
      return
    }

    const missingFields = REQUIRED_FIELDS.filter(
      field => !(field in sample)
    )

    if (missingFields.length > 0) {
      logWarn(
        `${LOG_TAG} 필수 필드 누락: ${missingFields.join(', ')}`,
        sample
      )
    }
  }

  /**
   * ✅ 개선: API 응답 검증 및 데이터 업데이트
   * @param {unknown} data - API 응답 데이터
   */
  function updateStoreData(data) {
    // 1. 배열 타입 검증
    if (!Array.isArray(data)) {
      throw new Error(
        `${ERRORS.INVALID_RESPONSE}: expected array, got ${typeof data}`
      )
    }

    // 2. 샘플 아이템 구조 검증
    if (data.length > 0) {
      validateSampleItem(data[0])
    }

    // 3. 빈 배열 경고
    if (data.length === 0) {
      logWarn(`${LOG_TAG} API 응답이 비어있음`)
    }

    // 4. 상태 업데이트
    materialScoreData.value = data
    loaded.value = true
    lastUpdated.value = new Date() // ✅ 추가
    error.value = null             // ✅ 에러 초기화

    logDebug(`${LOG_TAG} 데이터 업데이트 완료 (${data.length}개)`)
  }

  /**
   * 로딩 상태 초기화
   */
  function resetLoadingState() {
    loading.value = false
    // ✅ 개선: fetchPromise 비동기 정리
    setTimeout(() => {
      fetchPromise = null
    }, 0)
  }

  /* =========================================================
   * 4) Actions
   * ======================================================= */
  /**
   * ✅ 개선: 서버에서 점수 데이터 조회
   * @returns {Promise<void>}
   */
  async function fetchMaterialScores() {
    // 캐시 확인
    if (loaded.value) {
      logDebug(`${LOG_TAG} 캐시된 데이터 사용 (${dataCount.value}개)`)
      return
    }

    // 진행 중인 요청 확인
    if (fetchPromise) {
      logDebug(`${LOG_TAG} 진행 중인 요청 대기`)
      return fetchPromise
    }

    loading.value = true
    error.value = null // ✅ 에러 초기화

    fetchPromise = (async () => {
      try {
        const data = await materialScoreApi.getAllMaterialScores()
        updateStoreData(data)
        
        logInfo(`${LOG_TAG} 점수 데이터 로드 완료 (${data.length}개)`)
      } catch (err) {
        error.value = err // ✅ 에러 저장
        logError(`${LOG_TAG} ${ERRORS.FETCH_FAILED}:`, err)
        throw err
      } finally {
        resetLoadingState()
      }
    })()

    return fetchPromise
  }

  /**
   * ✅ 개선: 서버 캐시 리로드 후 재조회
   * @returns {Promise<void>}
   */
  async function reloadMaterialScores() {
    loading.value = true
    error.value = null

    try {
      // 1단계: 기존 데이터 초기화 (선택적)
      materialScoreData.value = []
      loaded.value = false

      // 2단계: 서버 캐시 리로드
      await materialScoreApi.reloadMaterialScores()
      logInfo(`${LOG_TAG} 서버 캐시 리로드 완료`)

      // 3단계: 갱신된 데이터 조회
      const data = await materialScoreApi.getAllMaterialScores()
      updateStoreData(data)

      logInfo(`${LOG_TAG} 데이터 재로딩 완료 (${data.length}개)`)
    } catch (err) {
      error.value = err
      logError(`${LOG_TAG} ${ERRORS.RELOAD_FAILED}:`, err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * ✅ 개선: 에러 초기화
   */
  function clearError() {
    error.value = null
    logDebug(`${LOG_TAG} 에러 상태 초기화`)
  }

  /**
   * 스토어 초기화
   */
  function reset() {
    materialScoreData.value = []
    loaded.value = false
    loading.value = false
    error.value = null
    lastUpdated.value = null
    fetchPromise = null
    
    logDebug(`${LOG_TAG} 스토어 초기화 완료`)
  }

  /* =========================================================
   * 5) Return (공개 API)
   * ======================================================= */
  return {
    // ✅ Getters만 노출 (State 직접 노출 안 함)
    materialScoreList,
    isLoading,
    isLoaded,
    dataCount,
    isEmpty,        // ✅ 추가
    hasData,        // ✅ 추가
    hasError,       // ✅ 추가
    errorMessage,   // ✅ 추가
    lastUpdated: computed(() => lastUpdated.value), // ✅ 추가

    // Actions
    fetchMaterialScores,
    reloadMaterialScores,
    clearError,     // ✅ 추가
    reset,
  }
})